import { Input, InputProps } from '@/app/components/Input'
import { SearchBackground } from '@/app/icons/SearchBackground'
import { CircularProgress } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { MdTune } from 'react-icons/md'

type FormValues = {
  searchTerm: string
}

type TextSearchInputProps = Omit<InputProps, 'onChange'> & {
  onSearch: (searchTerm: string) => Promise<void>
  defaultValue?: string
  noIcon?: boolean
  onFilterClick?: (term: string) => void
  hideFilters?: boolean
}

export const TextSearchInput = ({
  onSearch,
  defaultValue = '',
  noIcon = false,
  onFilterClick,
  hideFilters = false,
  ...props
}: TextSearchInputProps) => {
  const t = useTranslations('header')

  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      searchTerm: defaultValue,
    },
  })

  const term = watch('searchTerm')

  const onSubmit = async (data: FormValues) => {
    const trimmedValue = data.searchTerm.trim()
    if (trimmedValue) {
      await onSearch(trimmedValue)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSubmit(onSubmit)()
    }
  }

  const handleIconClick = () => {
    handleSubmit(onSubmit)()
  }

  return (
    <Input
      {...props}
      {...register('searchTerm')}
      type="text"
      onKeyDown={handleKeyDown}
      placeholder={t('text_search_placeholder')}
      disabled={isSubmitting}
      className={`!max-h-[50px] !rounded-[10px] !py-[6px] !pl-2 !pr-[6px] text-sm focus-within:outline-mydra-black md:!rounded-xl md:!py-[9px] md:!pl-6 md:!pr-2 md:text-base ${props.className ?? ''}`}
      endDecoration={
        !noIcon ? (
          <div className="flex items-center justify-center gap-1 md:gap-2">
            {!hideFilters && (
              <button
                type="button"
                onClick={() => onFilterClick?.(term)}
                className="flex items-center gap-1"
                disabled={isSubmitting}
              >
                <MdTune className="size-6 md:size-5" />
                <span className="hidden text-sm md:inline">{t('filters')}</span>
              </button>
            )}
            <button
              type="button"
              onClick={handleIconClick}
              disabled={isSubmitting}
              className="relative"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <CircularProgress
                    size={33}
                    className="text-mydra-black md:hidden"
                  />
                  <CircularProgress
                    size={40}
                    className="hidden text-mydra-black md:block"
                  />
                </div>
              ) : (
                <>
                  <SearchBackground
                    width={33}
                    height={33}
                    className="md:hidden"
                  />
                  <SearchBackground
                    width={40}
                    height={40}
                    className="hidden md:block"
                  />
                </>
              )}
            </button>
          </div>
        ) : null
      }
    />
  )
}
